/*global CustomFunctions */
import { isGuid, formulaErrorHandling } from "../../shared/helpers";
import { FormulaErrorFormat } from "../../shared/types";
import { observerForCustomFunction } from "../../shared/query-client";
import { PARAMETER_BY_ID_BATCHER, ENTRY_REVISIONS_BY_ENTRY_ID_BATCHER } from "../../shared/batchers";

/**
 * Gets DDB parameter value.
 * @customfunction PARAMETER.VALUE
 * @param parameterId The parameter ID.
 * @param [revisionId] The parameter revision ID. If omitted, the latest revision is returned.
 * @returns The parameter value at the specified revision.
 * @streaming
 */
export function parameterValue(
  parameterId: string,
  revisionId: string | null,
  invocation: CustomFunctions.StreamingInvocation<any>
): void {
  if (!isGuid(parameterId)) {
    invocation.setResult(formulaErrorHandling(FormulaErrorFormat.PARAMETER_ID));
    return;
  }

  if (revisionId !== null && !isGuid(revisionId)) {
    invocation.setResult(formulaErrorHandling(FormulaErrorFormat.INVALID_REVISION));
    return;
  }

  if (revisionId === null) {
    observerForCustomFunction(invocation, {
      queryKey: ["parameter", parameterId],
      queryFn: async () => PARAMETER_BY_ID_BATCHER.fetch(parameterId),
      select: ({ selected_entry }) => selected_entry?.values?.at(0)?.value ?? "",
    });
  } else {
    observerForCustomFunction(invocation, {
      queryKey: ["parameter", parameterId, revisionId],
      queryFn: async () =>
        PARAMETER_BY_ID_BATCHER.fetch(parameterId).then(({ selected_entry }) =>
          ENTRY_REVISIONS_BY_ENTRY_ID_BATCHER.fetch(selected_entry?.id ?? "")
        ),
      select: ({ values }) => values.at(0)?.value ?? "",
    });
  }
}

/**
 * Gets DDB parameter name.
 * @customfunction PARAMETER.PARAMETER_TYPE_NAME
 * @param parameterId The parameter ID.
 * @returns The parameter name.
 * @streaming
 */
export function parameterParameterTypeName(
  parameterId: string,
  invocation: CustomFunctions.StreamingInvocation<string>
): void {
  if (!isGuid(parameterId)) {
    invocation.setResult(formulaErrorHandling(FormulaErrorFormat.PARAMETER_ID));
    return;
  }

  observerForCustomFunction(invocation, {
    queryKey: ["parameter", parameterId],
    queryFn: async () => PARAMETER_BY_ID_BATCHER.fetch(parameterId),
    select: ({ parameter_type }) => parameter_type.name,
  });
}

/**
 * Gets DDB parameter source ID.
 * @customfunction PARAMETER.SOURCE_ID
 * @param parameterId The parameter ID.
 * @param [revisionId] The parameter revision ID. If omitted, the latest revision is returned.
 * @returns The parameter source ID.
 * @streaming
 */
export function parameterSourceId(
  parameterId: string,
  revisionId: string | null,
  invocation: CustomFunctions.StreamingInvocation<string>
): void {
  if (!isGuid(parameterId)) {
    invocation.setResult(formulaErrorHandling(FormulaErrorFormat.PARAMETER_ID));
    return;
  }

  if (revisionId !== null && !isGuid(revisionId)) {
    invocation.setResult(formulaErrorHandling(FormulaErrorFormat.INVALID_REVISION));
    return;
  }

  if (revisionId === null) {
    observerForCustomFunction(invocation, {
      queryKey: ["parameter", parameterId],
      queryFn: async () => PARAMETER_BY_ID_BATCHER.fetch(parameterId),
      select: ({ selected_entry }) => selected_entry?.source?.id ?? "",
    });
  } else {
    observerForCustomFunction(invocation, {
      queryKey: ["parameter", parameterId, revisionId],
      queryFn: async () =>
        PARAMETER_BY_ID_BATCHER.fetch(parameterId).then(({ selected_entry }) =>
          ENTRY_REVISIONS_BY_ENTRY_ID_BATCHER.fetch(selected_entry?.id ?? "")
        ),
      select: ({ source }) => source.id,
    });
  }
}

/**
 * Gets DDB parameter status.
 * @customfunction PARAMETER.STATUS
 * @param parameterId The parameter ID.
 * @param [revisionId] The parameter revision ID. If omitted, the latest revision is returned.
 * @returns The parameter status.
 * @streaming
 */
export function parameterStatus(
  parameterId: string,
  revisionId: string | null,
  invocation: CustomFunctions.StreamingInvocation<string>
): void {
  if (!isGuid(parameterId)) {
    invocation.setResult(formulaErrorHandling(FormulaErrorFormat.PARAMETER_ID));
    return;
  }

  if (revisionId !== null && !isGuid(revisionId)) {
    invocation.setResult(formulaErrorHandling(FormulaErrorFormat.INVALID_REVISION));
    return;
  }

  if (revisionId === null) {
    observerForCustomFunction(invocation, {
      queryKey: ["parameter", parameterId],
      queryFn: async () => PARAMETER_BY_ID_BATCHER.fetch(parameterId),
      select: ({ selected_entry }) => selected_entry?.status || "",
    });
  } else {
    observerForCustomFunction(invocation, {
      queryKey: ["parameter", parameterId, revisionId],
      queryFn: async () =>
        PARAMETER_BY_ID_BATCHER.fetch(parameterId).then(({ selected_entry }) =>
          ENTRY_REVISIONS_BY_ENTRY_ID_BATCHER.fetch(selected_entry?.id || "")
        ),
      select: ({ status }) => status,
    });
  }
}

/**
 * Gets DDB parameter unit symbol.
 * @customfunction PARAMETER.UNIT_SYMBOL
 * @param parameterId The parameter ID.
 * @param [revisionId] The parameter revision ID. If omitted, the latest revision is returned.
 * @returns The parameter unit symbol.
 * @streaming
 */
export function parameterUnitSymbol(
  parameterId: string,
  revisionId: string | null,
  invocation: CustomFunctions.StreamingInvocation<string>
): void {
  if (!isGuid(parameterId)) {
    invocation.setResult(formulaErrorHandling(FormulaErrorFormat.PARAMETER_ID));
    return;
  }

  if (revisionId !== null && !isGuid(revisionId)) {
    invocation.setResult(formulaErrorHandling(FormulaErrorFormat.INVALID_REVISION));
    return;
  }

  if (revisionId === null) {
    observerForCustomFunction(invocation, {
      queryKey: ["parameter", parameterId],
      queryFn: async () => PARAMETER_BY_ID_BATCHER.fetch(parameterId),
      select: ({ selected_entry }) => selected_entry?.values.at(0)?.unit?.symbol ?? "",
    });
  } else {
    observerForCustomFunction(invocation, {
      queryKey: ["parameter", parameterId, revisionId],
      queryFn: async () =>
        PARAMETER_BY_ID_BATCHER.fetch(parameterId).then(({ selected_entry }) =>
          ENTRY_REVISIONS_BY_ENTRY_ID_BATCHER.fetch(selected_entry?.id ?? "")
        ),
      select: ({ values }) => values.at(0)?.unit?.symbol ?? "",
    });
  }
}

CustomFunctions.associate("PARAMETER.VALUE", parameterValue);
CustomFunctions.associate("PARAMETER.PARAMETER_TYPE_NAME", parameterParameterTypeName);
CustomFunctions.associate("PARAMETER.SOURCE_ID", parameterSourceId);
CustomFunctions.associate("PARAMETER.STATUS", parameterStatus);
CustomFunctions.associate("PARAMETER.UNIT_SYMBOL", parameterUnitSymbol);